import React from 'react';
import { reportClientError } from '../../utils/errorReporter';
import './ErrorBoundary.css';

/**
 * ErrorBoundary - Evita que un error de render tumbe TODA la aplicacion.
 *
 * Sin un boundary, cualquier excepcion en render deja React sin arbol montado:
 * el usuario ve una pantalla en blanco (reporte de Farmatodo, 18-sep-2026,
 * donde la burbuja del asistente se llevo por delante el reporte de PowerBI).
 *
 * Ademas manda el error a /api/track (action='js_error') para poder
 * diagnosticarlo despues sin depender de que el cliente abra la consola.
 *
 * Props:
 *   username  - para la telemetria
 *   module    - etiqueta del bloque protegido (floating_chat, app, ...)
 *   compact   - fallback chico (para la burbuja), no de pagina completa
 *   onReset   - callback opcional al pulsar "Reintentar"
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, message: '' };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, message: (error && error.message) || '' };
    }

    componentDidCatch(error, info) {
        reportClientError(this.props.username, this.props.module || 'app', error, {
            source: 'error_boundary',
            component_stack: (info && info.componentStack ? info.componentStack : '').slice(0, 2000),
        });
        console.error(`[ErrorBoundary:${this.props.module || 'app'}]`, error, info);
    }

    handleReset = () => {
        this.setState({ hasError: false, message: '' });
        if (this.props.onReset) this.props.onReset();
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <div className={`eb-fallback ${this.props.compact ? 'eb-fallback-compact' : ''}`}>
                <div className="eb-title">Algo falló al mostrar esta sección</div>
                <div className="eb-text">
                    El resto de la plataforma sigue funcionando. Ya registramos el
                    detalle del error para revisarlo.
                </div>
                {this.state.message && (
                    <div className="eb-detail">{this.state.message}</div>
                )}
                <div className="eb-actions">
                    <button className="eb-btn" onClick={this.handleReset}>Reintentar</button>
                    <button
                        className="eb-btn eb-btn-secondary"
                        onClick={() => window.location.reload()}
                    >
                        Recargar página
                    </button>
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
